/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AxiosResponse } from 'axios';

import {
  TableList,
  Container,
  Filter,
  Modal,
  TableListModal,
  ExportColumnsList,
} from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import * as XLSX from 'xlsx';
import api from '~/services/api';
import { KrClose, KrEye } from '~/components/KoroIcons';
import Loading from '~/components/Loading';
import formatDate from '~/utils/formatDate';
import Toast from '~/utils/toast';

interface ITicketData {
  voucher: string;
  id: number;
  code: number;
  status: string;
  order_code: number;
  batch_name: string;
  name: string;
  document: string;
  phone: string;
  email: string;
  birthdate: string;
  gender: string;
  event_id: number;
  event_name: string;
  event_date: string;
  modality_name: string;
  category_name: string;
  kit?: string;
  order_date: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  user_document: string;
  user_birthdate: string;
  user_gender: string;
  city: string;
  state: string;
  checkin_id?: number;
  read?: string;
  has_questions: boolean;
}

interface IQuestion {
  id: number;
  question: string;
  answer: string;
}

interface IAnswerResponse {
  id: number;
  text: string;
}

interface IQuestionResponse {
  id: number;
  name: string;
  order: number;
  answers: IAnswerResponse[];
}

interface ITicket {
  gender: string;
  birthdate: string;
  buyer_state?: string;
  buyer_city?: string;
  buyer_gender?: string;
  buyer_birthdate?: string;
  buyer_document?: string;
  read?: string;
  check_in_id?: number;
  batch_name: string;
  id: string;
  ticket_id: number;
  status: string;
  order: number;
  name: string;
  document: string;
  phone: string;
  email: string;
  event_id: number;
  event_name: string;
  event_date: string;
  modality?: string;
  category: string;
  buy_date: string;
  buyer_name: string;
  buyer_phone: string;
  buyer_email: string;
  kit: string;
  has_questions: boolean;
}

interface IExportTicket {
  voucher: any;
  gender: string;
  birthdate: string;
  buyer_state?: string;
  buyer_city?: string;
  buyer_gender?: string;
  buyer_birthdate?: string;
  buyer_document?: string;
  read?: string;
  check_in_id?: number;
  batch_name: string;
  id: string;
  ticket_id?: number;
  status: string;
  order: number;
  name: string;
  document: string;
  phone: string;
  email: string;
  event_id: number;
  event_name: string;
  event_date: string;
  modality?: string;
  category: string;
  buy_date: string;
  buyer_name: string;
  buyer_phone: string;
  buyer_email: string;
  kit: string;
  questions: IQuestion[];
}

interface IDataItem {
  id: string;
  name: string;
  status: string;
  document: string;
  phone: string;
  email: string;
  event_id: number;
  event_name: string;
  event_date: string;
  modality?: string;
  category: string;
  kit: string;
  [key: string]: any;
}

interface IExportResult {
  data: IDataItem[];
  failedQuestionRequests: number;
}

interface ITicketResponse {
  data: ITicketData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

const Users: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const [tickets, setTickets] = useState<ITicket[]>([]);
  const [statusSelected, setStatusSelected] = useState<string | undefined>();
  const [event, setEvent] = useState<string | undefined>();
  const [eventId, setEventId] = useState<string | undefined>();
  const [nameTicket, setNameTicket] = useState<string | undefined>();
  const [documentTicket, setDocumentTicket] = useState<string | undefined>();
  const [orderTicket, setOrderTicket] = useState<string | undefined>();
  const [ticketSelected, setTicketSelected] = useState<string | undefined>();
  const [voucherTicket, setVoucherTicket] = useState<string | undefined>();
  const [questions, setQuestions] = useState<IQuestion[]>([]);
  const [show, setShow] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [dataCsv, setDataCsv] = useState<IDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState<
    Array<{ name: string; selector: string }>
  >([]);
  const [selectedSelectors, setSelectedSelectors] = useState<Set<string>>(
    new Set()
  );
  const [pendingExportType, setPendingExportType] = useState<'csv' | 'xls'>(
    'csv'
  );
  const [onlyValidatedTickets, setOnlyValidatedTickets] = useState(true);

  const formatKitName = useCallback((kit: string | undefined): string => {
    if (!kit) return 'Não Possui';
    return kit.toLowerCase().includes('baby')
      ? `Babylook: ${kit.replace('_BABY', '')}`
      : !Number.isNaN(parseInt(kit, 10))
      ? `${kit} Anos`
      : kit;
  }, []);

  const handleClick = useCallback(async (ticketId) => {
    const response = await api.get<IQuestionResponse[]>(
      `questions/tickets/${ticketId}`
    );
    const questionsData = response.data.map<IQuestion>((question) => {
      return {
        id: question.order,
        question: question.name,
        answer: question.answers[0]?.text || '-',
      };
    });
    setQuestions(questionsData);
    setShow(true);
  }, []);

  const columnsModal = useMemo<IColumn[]>(
    () => [
      {
        name: 'Id',
        selector: 'id',
        sortable: false,
      },

      {
        name: 'Perguntas',
        selector: 'question',
        sortable: false,
      },
      {
        name: 'Respostas',
        selector: 'answer',
        sortable: false,
      },
    ],
    []
  );

  const status = useMemo(
    () => [
      {
        id: 'Disponível',
        value: 'Não Preenchido',
        selected: false,
      },
      {
        id: 'Usado',
        value: 'Preenchido',
        selected: false,
      },
      {
        id: 'Processando',
        value: 'Processando',
        selected: false,
      },
      {
        id: 'Devolvido',
        value: 'Devolvido',
        selected: false,
      },
      {
        id: 'Cancelado',
        value: 'Cancelado',
        selected: false,
      },
      {
        id: 'Cortesia',
        value: 'Cortesia',
        selected: false,
      },
    ],
    []
  );

  const handleLoadTickets = useCallback(
    async (
      page,
      statusData?: string,
      eventData?: string,
      quantityData?: number,
      event_id?: string,
      name?: string,
      document?: string,
      order?: string,
      voucher?: string,
      ticketFilter?: string
    ) => {
      setLoading(true);

      const response: AxiosResponse<ITicketResponse> = await api
        .get(`tickets/events/organizer`, {
          params: {
            page,
            status: statusData ? [statusData] : [],
            event: eventData,
            allEventsStatus: true,
            quantity: quantityData,
            event_id,
            name,
            document,
            order_id: order?.slice(12) || order,
            ticket_id: ticketFilter?.slice(12) || ticketFilter,
            voucher,
          },
        })
        .finally(() => setLoading(false));

      const dataTicket = response.data.data.map((ticket) => {
        return {
          id: `${ticket.code}`,
          status: ticket.status,
          order: ticket.order_code,
          voucher: ticket.voucher || '-',
          ticket_id: ticket.id,
          name: ticket.name || '-',
          document: ticket.document || '-',
          phone: ticket.phone || '-',
          email: ticket.email || '-',
          birthdate: ticket.birthdate
            ? formatDate(ticket.birthdate, 'dd/MM/yyyy')
            : '-',
          gender: ticket.gender || '-',
          event_id: ticket.event_id,
          batch_name: ticket.batch_name || '-',
          event_name: ticket.event_name || '-',
          event_date: formatDate(ticket.event_date, 'dd/MM/yyyy'),
          modality: ticket.modality_name || '-',
          category: ticket.category_name || '-',
          kit: formatKitName(ticket.kit),
          buy_date: formatDate(ticket.order_date, 'dd/MM/yyyy'),
          buyer_name: ticket.user_name || '-',
          buyer_phone: ticket.user_phone || '-',
          buyer_email: ticket.user_email || '-',
          buyer_document: ticket.user_document || '-',
          buyer_birthdate: ticket.user_birthdate
            ? formatDate(ticket.user_birthdate, 'dd/MM/yyyy')
            : '-',
          buyer_gender: ticket.user_gender || '-',
          buyer_city: ticket.city || '-',
          buyer_state: ticket.state || '-',
          check_in_id: ticket.checkin_id || undefined,
          read: ticket.read
            ? formatDate(ticket.read, 'dd/MM/yyyy HH:mm:ss')
            : '-',
          has_questions: ticket.has_questions,
        };
      });

      setTickets(dataTicket);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    [formatKitName]
  );

  const handleClose = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    handleLoadTickets(1);
  }, [handleLoadTickets]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: 'Inscrição',
        selector: 'id',
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        sortable: true,
        cell: (row) => (
          <span>
            {(row.status === 'Usado' && 'Preenchido') ||
              (row.status === 'Disponível' && 'Não Preenchido') ||
              row.status}
          </span>
        ),
      },
      {
        name: 'Pedido',
        selector: 'order',
        sortable: true,
      },
      {
        name: 'Cupom',
        selector: 'voucher',
        sortable: true,
      },
      {
        name: 'Lote',
        selector: 'batch_name',
        sortable: true,
      },
      {
        name: 'Nome',
        selector: 'name',
        sortable: true,
      },
      {
        name: 'Documento',
        selector: 'document',
        sortable: true,
      },
      {
        name: 'Telefone',
        selector: 'phone',
        sortable: true,
      },
      {
        name: 'E-mail',
        selector: 'email',
        sortable: true,
      },
      {
        name: 'Nascimento',
        selector: 'birthdate',
        sortable: true,
      },
      {
        name: 'Gênero',
        selector: 'gender',
        sortable: true,
      },
      {
        name: 'Id do evento',
        selector: 'event_id',
        sortable: false,
      },
      {
        name: 'Nome do evento',
        selector: 'event_name',
        sortable: true,
      },
      {
        name: 'Data do evento',
        selector: 'event_date',
        sortable: false,
      },
      {
        name: 'Modalidade',
        selector: 'modality',
        sortable: true,
      },
      {
        name: 'Categoria',
        selector: 'category',
        sortable: true,
      },
      {
        name: 'Kit',
        selector: 'kit',
        sortable: true,
      },
      {
        name: 'Data compra',
        selector: 'buy_date',
        sortable: false,
      },
      {
        name: 'Nome comprador',
        selector: 'buyer_name',
        sortable: true,
      },
      {
        name: 'Fone comprador',
        selector: 'buyer_phone',
        sortable: true,
      },
      {
        name: 'E-mail comprador',
        selector: 'buyer_email',
        sortable: true,
      },
      {
        name: 'Doc comprador',
        selector: 'buyer_document',
        sortable: true,
      },
      {
        name: 'Nascimento comprador',
        selector: 'buyer_birthdate',
        sortable: true,
      },
      {
        name: 'Gênero comprador',
        selector: 'buyer_gender',
        sortable: true,
      },
      {
        name: 'Cidade',
        selector: 'buyer_city',
        sortable: true,
      },
      {
        name: 'UF',
        selector: 'buyer_state',
        sortable: true,
      },
      {
        name: 'Check-in Id',
        selector: 'check_in_id',
        sortable: true,
      },
      {
        name: 'Data/Hora Check-in',
        selector: 'read',
        sortable: true,
      },
      {
        name: 'Perguntas',
        selector: 'questions',
        cell: (row: ITicket) => (
          <button
            type="button"
            onClick={() => handleClick(row.ticket_id)}
            disabled={!row.has_questions}
            className={`${
              row.has_questions ? 'visible' : 'opacity-50'
            } btn-icons border-0`}
          >
            <KrEye size={16} color="#fff" />
          </button>
        ),
      },
    ],
    [handleClick]
  );

  const handleChangePage = useCallback(
    (page) => {
      handleLoadTickets(
        page,
        statusSelected,
        event,
        quantity,
        eventId,
        nameTicket,
        documentTicket,
        orderTicket,
        voucherTicket,
        ticketSelected
      );
      setPageSelected(page);
    },
    [
      handleLoadTickets,
      statusSelected,
      event,
      quantity,
      eventId,
      nameTicket,
      documentTicket,
      orderTicket,
      voucherTicket,
      ticketSelected,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setStatusSelected(
        formData.statusTicket !== 'todos' ? formData.statusTicket : undefined
      );
      setEvent(
        formData.event === 'Todos' ? undefined : formData.event || undefined
      );
      setEventId(formData.event_id === 'Todos' ? undefined : formData.event_id);
      setNameTicket(formData.name || undefined);
      setDocumentTicket(formData.document || undefined);
      setOrderTicket(formData.order || undefined);
      setTicketSelected(formData.ticket || undefined);
      setVoucherTicket(formData.codeVoucher || undefined);
      handleLoadTickets(
        1,
        formData.statusTicket !== 'todos' ? formData.statusTicket : undefined,
        formData.event === 'Todos' ? undefined : formData.event || undefined,
        quantity,
        formData.event === 'Todos' ? undefined : formData.event_id,
        formData.name || undefined,
        formData.document || undefined,
        formData.order || undefined,
        formData.codeVoucher || undefined,
        formData.ticket || undefined
      );
      setPageSelected(1);
    },
    [handleLoadTickets, quantity]
  );

  const handleChangeDataCsv = useCallback((data: IExportTicket[]) => {
    const dataAux = data.map<IDataItem>((ticket) => {
      const questionsData = ticket.questions.reduce((acc, question, index) => {
        acc[`questao ${index.toString().padStart(2, '0')}`] = `Q ${question.id
          .toString()
          .padStart(2, '0')}`;
        acc[`resposta ${index.toString().padStart(2, '0')}`] = question.answer;
        return acc;
      }, {} as any);

      return {
        id: ticket.id,
        status: ticket.status,
        order: ticket.order,
        voucher: ticket.voucher,
        batch_name: ticket.batch_name,
        name: ticket.name,
        document: ticket.document,
        phone: ticket.phone,
        email: ticket.email,
        birthdate: ticket.birthdate,
        gender: ticket.gender,
        event_id: ticket.event_id,
        event_name: ticket.event_name,
        event_date: ticket.event_date,
        modality: ticket.modality,
        category: ticket.category,
        kit: ticket.kit,
        buy_date: ticket.buy_date,
        buyer_name: ticket.buyer_name,
        buyer_phone: ticket.buyer_phone,
        buyer_email: ticket.buyer_email,
        buyer_document: ticket.buyer_document,
        buyer_birthdate: ticket.buyer_birthdate,
        buyer_gender: ticket.buyer_gender,
        buyer_city: ticket.buyer_city,
        buyer_state: ticket.buyer_state,
        check_in_id: ticket.check_in_id,
        read: ticket.read,
        ...questionsData,
      };
    });

    setDataCsv(dataAux);

    return dataAux;
  }, []);

  const columnsCsv = useMemo<IColumn[]>(() => {
    const questionKeys = Array.from(
      new Set(
        dataCsv.flatMap((item) =>
          Object.keys(item).filter((key) =>
            /^questao \d+$|^resposta \d+$/.test(key)
          )
        )
      )
    );

    return [
      {
        name: 'Inscrição',
        selector: 'id',
      },
      {
        name: 'Status',
        selector: 'status',
      },
      {
        name: 'Pedido',
        selector: 'order',
      },
      {
        name: 'Cupom',
        selector: 'voucher',
      },
      {
        name: 'Lote',
        selector: 'batch_name',
      },
      {
        name: 'Nome',
        selector: 'name',
      },
      {
        name: 'Documento',
        selector: 'document',
      },
      {
        name: 'Telefone',
        selector: 'phone',
      },
      {
        name: 'E-mail',
        selector: 'email',
      },
      {
        name: 'Nascimento',
        selector: 'birthdate',
      },
      {
        name: 'Gênero',
        selector: 'gender',
      },
      {
        name: 'Id do evento',
        selector: 'event_id',
      },
      {
        name: 'Nome do evento',
        selector: 'event_name',
      },
      {
        name: 'Data do evento',
        selector: 'event_date',
      },
      {
        name: 'Modalidade',
        selector: 'modality',
      },
      {
        name: 'Categoria',
        selector: 'category',
      },
      {
        name: 'Kit',
        selector: 'kit',
      },
      {
        name: 'Data compra',
        selector: 'buy_date',
      },
      {
        name: 'Nome comprador',
        selector: 'buyer_name',
      },
      {
        name: 'Fone comprador',
        selector: 'buyer_phone',
      },
      {
        name: 'E-mail comprador',
        selector: 'buyer_email',
      },
      {
        name: 'Doc comprador',
        selector: 'buyer_document',
      },
      {
        name: 'Nascimento comprador',
        selector: 'buyer_birthdate',
      },
      {
        name: 'Gênero comprador',
        selector: 'buyer_gender',
      },
      {
        name: 'Cidade',
        selector: 'buyer_city',
      },
      {
        name: 'UF',
        selector: 'buyer_state',
      },
      {
        name: 'Check-in Id',
        selector: 'check_in_id',
      },
      {
        name: 'Data/Hora Check-in',
        selector: 'read',
      },
      ...questionKeys.map((questionKey) => ({
        name: '',
        selector: questionKey,
      })),
    ];
  }, [dataCsv]);

  const handleClickExportCsv = useCallback(async (): Promise<IExportResult> => {
    setLoading(true);
    try {
      // Usa o mesmo endpoint da tabela (sem exportData) com quantity alto
      // para buscar todos os registros filtrados, já que exportData:true
      // não suporta filtros corretamente.
      const response: AxiosResponse<ITicketResponse> = await api.get(
        `tickets/events/organizer`,
        {
          params: {
            page: 1,
            status: statusSelected ? [statusSelected] : [],
            event,
            allEventsStatus: true,
            name: nameTicket,
            document: documentTicket,
            order_id: orderTicket?.slice(12) || orderTicket,
            ticket_id: ticketSelected?.slice(12) || ticketSelected,
            voucher: voucherTicket,
            event_id: eventId,
            quantity: 100000,
          },
        }
      );

      const ticketsToExport = onlyValidatedTickets
        ? response.data.data.filter((ticket) =>
            ['Usado', 'Disponível', 'Cortesia'].includes(ticket.status)
          )
        : response.data.data;

      const dataTicket: IExportTicket[] = ticketsToExport.map((ticket) => ({
        id: `#${ticket.code}`,
        ticket_id: ticket.id,
        status:
          (ticket.status === 'Usado' && 'Preenchido') ||
          (ticket.status === 'Disponível' && 'Não Preenchido') ||
          ticket.status,
        order: ticket.order_code,
        voucher: ticket.voucher || '-',
        batch_name: ticket.batch_name || '-',
        name: ticket.name || '-',
        document: ticket.document || '-',
        phone: ticket.phone || '-',
        email: ticket.email || '-',
        birthdate: ticket.birthdate
          ? formatDate(ticket.birthdate, 'dd/MM/yyyy')
          : '-',
        gender: ticket.gender || '-',
        event_id: ticket.event_id,
        event_name: ticket.event_name || '-',
        event_date: formatDate(ticket.event_date, 'dd/MM/yyyy'),
        modality: ticket.modality_name || '-',
        category: ticket.category_name || '-',
        kit: formatKitName(ticket.kit),
        buy_date: formatDate(ticket.order_date, 'dd/MM/yyyy'),
        buyer_name: ticket.user_name || '-',
        buyer_phone: ticket.user_phone || '-',
        buyer_email: ticket.user_email || '-',
        buyer_document: ticket.user_document || '-',
        buyer_birthdate: ticket.user_birthdate
          ? formatDate(ticket.user_birthdate, 'dd/MM/yyyy')
          : '-',
        buyer_gender: ticket.user_gender || '-',
        buyer_city: ticket.city || '-',
        buyer_state: ticket.state || '-',
        check_in_id: ticket.checkin_id,
        read: ticket.read
          ? formatDate(ticket.read, 'dd/MM/yyyy HH:mm:ss')
          : '-',
        questions: [],
      }));

      // Limita a concorrência para evitar sobrecarregar a API em exportações grandes.
      const ticketsWithQuestions = ticketsToExport
        .map((ticket, idx) => ({ ticketId: ticket.id, idx }))
        .filter((_, idx) => ticketsToExport[idx].has_questions);
      let failedQuestionRequests = 0;

      const batchSize = 10;
      const loadQuestionBatch = (start: number): Promise<void> => {
        if (start >= ticketsWithQuestions.length) return Promise.resolve();

        const batch = ticketsWithQuestions.slice(start, start + batchSize);
        return Promise.allSettled(
          batch.map(({ ticketId }) =>
            api.get<IQuestionResponse[]>(`questions/tickets/${ticketId}`)
          )
        ).then((results) => {
          results.forEach((result, i) => {
            if (result.status === 'rejected') {
              failedQuestionRequests += 1;
              console.error(
                `Erro ao buscar perguntas da inscrição ${batch[i].ticketId}:`,
                result.reason
              );
              return;
            }

            const { idx } = batch[i];
            dataTicket[idx].questions = result.value.data.map<IQuestion>(
              (q) => ({
                id: q.order,
                question: q.name,
                answer: q.answers[0]?.text || '-',
              })
            );
          });

          return loadQuestionBatch(start + batchSize);
        });
      };
      await loadQuestionBatch(0);

      const dataCsvAux = handleChangeDataCsv(dataTicket);
      return { data: dataCsvAux, failedQuestionRequests };
    } finally {
      setLoading(false);
    }
  }, [
    statusSelected,
    event,
    nameTicket,
    documentTicket,
    orderTicket,
    voucherTicket,
    ticketSelected,
    eventId,
    formatKitName,
    handleChangeDataCsv,
    onlyValidatedTickets,
  ]);

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      setQuantity(quantityData);
      handleLoadTickets(
        1,
        statusSelected,
        event,
        quantityData,
        eventId,
        nameTicket,
        documentTicket,
        orderTicket,
        voucherTicket,
        ticketSelected
      );
      setPageSelected(1);
    },
    [
      handleLoadTickets,
      statusSelected,
      event,
      eventId,
      nameTicket,
      documentTicket,
      orderTicket,
      voucherTicket,
      ticketSelected,
    ]
  );

  const handleOpenExportModal = useCallback(
    (type: 'csv' | 'xls') => {
      setPendingExportType(type);

      const staticCols: Array<{ name: string; selector: string }> = columnsCsv
        .filter(
          (c) =>
            typeof c.selector === 'string' &&
            !/^questao \d+$|^resposta \d+$/.test(c.selector)
        )
        .map((c) => ({
          name:
            typeof c.name === 'string' && c.name
              ? c.name
              : (c.selector as string),
          selector: c.selector as string,
        }));

      const cols = [
        ...staticCols,
        { name: 'Perguntas', selector: '__questions__' },
      ];
      const initialSelected = new Set(cols.map((c) => c.selector));

      setExportColumns(cols);
      setSelectedSelectors(initialSelected);
      setShowExportModal(true);
    },
    [columnsCsv]
  );

  const handleToggleSelector = useCallback((selector: string) => {
    setSelectedSelectors((prev) => {
      const next = new Set(prev);
      if (next.has(selector)) next.delete(selector);
      else next.add(selector);
      return next;
    });
  }, []);

  const handleConfirmExport = useCallback(async () => {
    setShowExportModal(false);

    try {
      const { data: rawData, failedQuestionRequests } =
        await handleClickExportCsv();
      if (!rawData || rawData.length === 0) {
        Toast.fire({
          icon: 'warning',
          title: 'Nenhum dado encontrado com os filtros aplicados',
        });
        return;
      }

      // Deriva as chaves de perguntas diretamente dos dados
      const questionKeys = Array.from(
        new Set(
          rawData.flatMap((item) =>
            Object.keys(item).filter((k) =>
              /^questao \d+$|^resposta \d+$/.test(k)
            )
          )
        )
      ).sort((a, b) => {
        const numA = parseInt(a.match(/\d+/)?.[0] ?? '0', 10);
        const numB = parseInt(b.match(/\d+/)?.[0] ?? '0', 10);
        if (numA !== numB) return numA - numB;
        // Mesmo número: 'questao' (q) vem antes de 'resposta' (r)
        return a.localeCompare(b);
      });
      const includeQuestions = selectedSelectors.has('__questions__');

      const mappedData = rawData.map((item) => {
        const newItem: { [key: string]: any } = {};
        exportColumns.forEach((col, idx) => {
          if (col.selector === '__questions__') {
            if (includeQuestions) {
              questionKeys.forEach((qKey) => {
                const match = qKey.match(/(questao|resposta) (\d+)/);
                if (match) {
                  const num = parseInt(match[2], 10) + 1;
                  const prefix =
                    match[1] === 'questao' ? 'Pergunta' : 'Resposta';
                  const header = `${prefix} ${num.toString().padStart(2, '0')}`;
                  newItem[header] = item[qKey] ?? '-';
                }
              });
            }
          } else if (selectedSelectors.has(col.selector)) {
            const headerName =
              col.name || `Extra ${String.fromCharCode(65 + idx)}`;
            newItem[headerName] = item[col.selector];
          }
        });
        return newItem;
      });

      if (pendingExportType === 'csv') {
        const keys = Object.keys(mappedData[0] || {});
        if (keys.length === 0) return;
        const csvContent = [
          keys.map((k) => `"${k}"`).join(','),
          ...mappedData.map((row) =>
            keys
              .map((k) => {
                const v = row[k] == null ? '' : String(row[k]);
                return `"${v.replace(/"/g, '""')}"`;
              })
              .join(',')
          ),
        ].join('\n');
        const blob = new Blob([`\uFEFF${csvContent}`], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Lista_de_Inscricoes.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const worksheet = XLSX.utils.json_to_sheet(mappedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
        XLSX.writeFile(workbook, 'Lista_de_Inscricoes.xlsx');
      }

      if (failedQuestionRequests > 0) {
        Toast.fire({
          icon: 'warning',
          title: 'Exportação concluída com respostas pendentes',
          text: `Não foi possível carregar as respostas de ${failedQuestionRequests} inscrição(ões). As células correspondentes foram marcadas com "-". Tente exportar novamente.`,
        });
      }
    } catch (err) {
      console.error('Erro ao exportar:', err);
      Toast.fire({
        icon: 'error',
        title: 'Erro ao exportar dados',
      });
    }
  }, [
    handleClickExportCsv,
    exportColumns,
    selectedSelectors,
    pendingExportType,
  ]);

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de inscrições</h1>
              <div>
                <button
                  type="button"
                  className="mt-3 mt-sm-0 mb-0 me-lg-4 h6 fw-normal text-white btn-new px-5 py-2"
                  onClick={() => handleOpenExportModal('csv')}
                >
                  Exportar .CSV
                </button>
                <button
                  type="button"
                  className="export-xls mt-3 mt-sm-0 mb-0 h6 fw-normal text-white btn-new px-5 py-2"
                  onClick={() => handleOpenExportModal('xls')}
                >
                  Exportar .XLS
                </button>
              </div>
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              event
              statusTicket
              document
              ticket
              order
              codeVoucher
              name
              statusOptions={status}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={tickets}
                        pagination
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        changeQuantity
                        onChangeQuantity={handleChangeQuantity}
                        showTotals
                        generalTotal={tableData.generalTotal}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Modal
        className="modal-filter"
        size="lg"
        centered
        show={show}
        onHide={handleClose}
      >
        <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4">
            <h2 className="title mb-0">Perguntas</h2>
          </Modal.Title>
          <button
            type="button"
            onClick={handleClose}
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="pt-0 px-3 px-sm-5">
          <div className="row">
            <div className="col-12">
              <hr />

              <TableListModal className="table-modal">
                <Table columns={columnsModal} data={questions} />
              </TableListModal>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-5" />
      </Modal>
      <Modal
        className="modal-filter"
        size="lg"
        centered
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
      >
        <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4">
            <h2 className="title mb-0">Selecionar colunas para exportar</h2>
          </Modal.Title>
          <button
            type="button"
            onClick={() => setShowExportModal(false)}
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="pt-2 px-3 px-sm-5">
          <div className="row">
            <div className="col-12">
              <hr />
              <div className="d-flex align-items-center gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-sm btn-select-all"
                  onClick={() =>
                    setSelectedSelectors(
                      new Set(exportColumns.map((c) => c.selector))
                    )
                  }
                >
                  Selecionar todas
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-select-all"
                  onClick={() => setSelectedSelectors(new Set())}
                >
                  Desmarcar todas
                </button>
                <label className="item ms-auto mb-0">
                  <input
                    type="checkbox"
                    checked={onlyValidatedTickets}
                    onChange={(changeEvent) =>
                      setOnlyValidatedTickets(changeEvent.target.checked)
                    }
                  />
                  <span>Somente inscrições validas</span>
                </label>
              </div>
              <ExportColumnsList>
                {exportColumns
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((col) => (
                    <label key={col.selector} className="item">
                      <input
                        type="checkbox"
                        checked={selectedSelectors.has(col.selector)}
                        onChange={() => handleToggleSelector(col.selector)}
                      />
                      <span>{col.name || col.selector}</span>
                    </label>
                  ))}
              </ExportColumnsList>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-5 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-secondary px-4"
            onClick={() => setShowExportModal(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn-export btn px-4 py-2 fw-normal h6 my-0 text-white"
            onClick={handleConfirmExport}
            disabled={selectedSelectors.size === 0}
          >
            Exportar
          </button>
        </Modal.Footer>
      </Modal>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Users;
